import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, Plus, Mail, CheckCircle, AlertTriangle, Copy, Loader2, Users, LogOut,
  Pencil, Power, ChevronRight, UserCog, RotateCw, Trash2, Undo2, Eye, EyeOff,
} from 'lucide-react';

export default function SuperAdminPage() {
  const { api, logout } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [form, setForm] = useState({ name: '', admin_email: '', admin_first_name: '', admin_last_name: '', sport: 'football' });
  const [showInvite, setShowInvite] = useState(null);
  const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '' });
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [editOrg, setEditOrg] = useState(null);
  const [editName, setEditName] = useState('');
  const [editSport, setEditSport] = useState('football');
  const [editing, setEditing] = useState(false);
  const [sports, setSports] = useState([]);
  const [detailOrg, setDetailOrg] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchOrgs = useCallback(async () => {
    try { const res = await api.get('/superadmin/organizations', { params: { include_deleted: showDeleted } }); setOrgs(res.data); }
    catch { toast.error('Failed to load organizations'); }
    finally { setLoading(false); }
  }, [api, showDeleted]);

  useEffect(() => {
    fetchOrgs();
    api.get('/superadmin/diagnostics').then(r => setDiagnostics(r.data)).catch(() => {});
    api.get('/sport-config/sports').then(r => setSports(r.data || [])).catch(() => setSports([]));
  }, [fetchOrgs, api]);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.admin_email.trim()) return toast.error('Organization name and admin email are required');
    setCreating(true);
    try {
      const res = await api.post('/superadmin/organizations', { ...form, frontend_origin: window.location.origin });
      setCreateResult(res.data);
      toast[res.data.invitation?.email_sent ? 'success' : 'warning'](res.data.invitation?.email_sent ? 'Organization created and invitation email sent!' : 'Organization created but email failed.');
      fetchOrgs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create organization'); }
    finally { setCreating(false); }
  };

  const handleInviteAdmin = async () => {
    if (!inviteForm.email.trim()) return toast.error('Admin email is required');
    setInviting(true);
    try {
      const res = await api.post(`/superadmin/organizations/${showInvite}/invite-admin`, { ...inviteForm, frontend_origin: window.location.origin });
      setInviteResult(res.data);
      toast[res.data.email_sent ? 'success' : 'warning'](res.data.email_sent ? 'Admin invitation email sent!' : 'Invitation created but email failed.');
      fetchOrgs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send invitation'); }
    finally { setInviting(false); }
  };

  const handleEditOrg = async () => {
    if (!editName.trim()) return toast.error('Name cannot be empty');
    setEditing(true);
    try {
      await api.patch(`/superadmin/organizations/${editOrg.id}`, { name: editName.trim(), sport: editSport });
      toast.success('Organization updated');
      setEditOrg(null);
      fetchOrgs();
    }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setEditing(false); }
  };

  // ─── Demo controls (Ravenna FC DEMO) ─────────────────────────────────────
  const [demoBusy, setDemoBusy] = useState(false);
  const handleDemoSeed = async () => {
    if (demoBusy) return;
    setDemoBusy(true);
    try {
      const res = await api.post('/superadmin/demo/seed');
      if (res.data?.status === 'already_seeded') {
        toast.info('Ravenna FC DEMO already exists — no changes made.');
      } else {
        toast.success('Ravenna FC DEMO generated successfully.');
      }
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate demo');
    } finally {
      setDemoBusy(false);
    }
  };
  const handleDemoReset = async () => {
    if (demoBusy) return;
    if (!window.confirm('Reset Ravenna FC DEMO? This will DELETE all current demo data and create a fresh copy. Other organizations are unaffected.')) return;
    setDemoBusy(true);
    try {
      await api.post('/superadmin/demo/reset');
      toast.success('Ravenna FC DEMO reset complete.');
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset demo');
    } finally {
      setDemoBusy(false);
    }
  };
  const handleDemoDelete = async () => {
    if (demoBusy) return;
    if (!window.confirm('Permanently DELETE Ravenna FC DEMO and all its data? This cannot be undone.')) return;
    setDemoBusy(true);
    try {
      const res = await api.delete('/superadmin/demo');
      if (res.data?.ok === false) {
        toast.info('Demo organization not found — nothing to delete.');
      } else {
        toast.success('Ravenna FC DEMO deleted.');
      }
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete demo');
    } finally {
      setDemoBusy(false);
    }
  };
  const demoOrg = orgs.find(o => o.is_demo);


  const handleDeleteOrg = async (org) => {
    try {
      await api.delete(`/superadmin/organizations/${org.id}`);
      toast.success(`"${org.name}" has been removed from the dashboard`);
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRestoreOrg = async (org) => {
    try {
      await api.post(`/superadmin/organizations/${org.id}/restore`);
      toast.success(`"${org.name}" has been restored`);
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  const handleResendInvite = async (orgId) => {
    try {
      const res = await api.post(`/superadmin/organizations/${orgId}/resend-invite`, { frontend_origin: window.location.origin });
      if (res.data.email_sent) {
        toast.success(`New invitation sent to ${res.data.email}`);
      } else {
        toast.warning('New invitation created but email failed. Copy the link below.');
      }
      // Show the invite link for manual copy
      setInviteResult(res.data);
      setShowInvite(orgId);
      fetchOrgs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend invite');
    }
  };

  const handleToggleActive = async (org) => {
    const newStatus = !org.is_active;
    if (!window.confirm(`Are you sure you want to ${newStatus ? 'reactivate' : 'deactivate'} "${org.name}"?${!newStatus ? ' All users will be disabled.' : ''}`)) return;
    try { await api.patch(`/superadmin/organizations/${org.id}`, { is_active: newStatus }); toast.success(`Organization ${newStatus ? 'reactivated' : 'deactivated'}`); fetchOrgs(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleViewDetail = async (orgId) => {
    setDetailOrg(orgId); setLoadingDetail(true);
    try { const res = await api.get(`/superadmin/organizations/${orgId}`); setDetailData(res.data); }
    catch { toast.error('Failed to load details'); setDetailOrg(null); }
    finally { setLoadingDetail(false); }
  };

  const resetCreate = () => { setShowCreate(false); setCreateResult(null); setForm({ name: '', admin_email: '', admin_first_name: '', admin_last_name: '', sport: 'football' }); };
  const resetInvite = () => { setShowInvite(null); setInviteResult(null); setInviteForm({ email: '', first_name: '', last_name: '' }); };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <div className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Building2 className="w-5 h-5 text-amber-500" />
            <h1 className="text-base font-bold tracking-tight">SuperAdmin</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="text-zinc-500 hover:text-zinc-300" data-testid="superadmin-logout">
            <LogOut className="w-4 h-4 mr-1.5" /> Logout
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Environment diagnostics banner */}
        {diagnostics && (
          <div className="p-3 rounded-lg bg-zinc-900/30 border border-zinc-800/50 text-[11px] text-zinc-500 flex flex-wrap gap-x-6 gap-y-1" data-testid="diagnostics-banner">
            <span>API: <span className="text-zinc-300">{diagnostics.api_version}</span></span>
            <span>Build: <span className="text-zinc-300">{diagnostics.build}</span></span>
            <span>DB: <span className="text-zinc-300">{diagnostics.db_name}</span></span>
            <span>Invite domain: <span className="text-zinc-300">{diagnostics.invite_link_domain || 'NONE'}</span></span>
            <span>Orgs: <span className="text-zinc-300">{diagnostics.organizations}</span></span>
            <span>Users: <span className="text-zinc-300">{diagnostics.users}</span></span>
            <span>Invites: <span className="text-zinc-300">{diagnostics.invitations_total} ({diagnostics.invitations_pending} pending)</span></span>
          </div>
        )}
        <div className="border border-cyan-500/30 rounded-lg p-4 mb-4 bg-cyan-500/5" data-testid="demo-controls">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h3 className="text-sm font-semibold text-cyan-300 uppercase tracking-wider flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Demo Controls — Ravenna FC DEMO
              </h3>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                {demoOrg
                  ? <>Demo is <span className="text-emerald-400">active</span>. Login as <code className="bg-zinc-800 px-1 rounded text-cyan-300">admin.ravenna.demo@fbperformance.com</code> with password <code className="bg-zinc-800 px-1 rounded text-cyan-300">Demo2026!</code></>
                  : <>Demo is <span className="text-amber-400">not generated</span>. Click "Generate Demo" to create the Ravenna FC DEMO organization (one-time, idempotent).</>}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={handleDemoSeed}
                disabled={demoBusy}
                size="sm"
                className="bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40"
                data-testid="demo-generate-btn"
              >
                {demoBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1.5" />}
                Generate Demo
              </Button>
              <Button
                onClick={handleDemoReset}
                disabled={demoBusy || !demoOrg}
                size="sm"
                variant="outline"
                className="bg-zinc-800 hover:bg-zinc-700 border-zinc-600 text-zinc-200"
                data-testid="demo-reset-btn"
              >
                <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Reset Demo
              </Button>
              <Button
                onClick={handleDemoDelete}
                disabled={demoBusy || !demoOrg}
                size="sm"
                variant="outline"
                className="bg-rose-500/10 hover:bg-rose-500/20 border-rose-500/40 text-rose-300"
                data-testid="demo-delete-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete Demo
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider">Organizations</h2>
            <button
              onClick={() => setShowDeleted(!showDeleted)}
              className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded transition-colors ${showDeleted ? 'bg-zinc-800 text-zinc-300' : 'text-zinc-600 hover:text-zinc-400'}`}
              data-testid="toggle-show-deleted"
            >
              {showDeleted ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              {showDeleted ? 'Showing deleted' : 'Show deleted'}
            </button>
          </div>
          <Button onClick={() => setShowCreate(true)} size="sm" className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="create-org-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Organization
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : orgs.length === 0 ? (
          <div className="text-center py-12 text-zinc-600">No organizations yet</div>
        ) : (
          <div className="space-y-3" data-testid="org-list">
            {orgs.map(org => (
              <Card key={org.id} className={`border-zinc-800 ${org.is_deleted ? 'bg-zinc-900/10 opacity-50' : org.is_active !== false ? 'bg-zinc-900/50' : 'bg-zinc-900/20 opacity-60'}`} data-testid={`org-card-${org.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold truncate ${org.is_deleted ? 'text-zinc-500 line-through' : 'text-zinc-100'}`} data-testid={`org-name-${org.id}`}>{org.name}</span>
                        {org.is_deleted && <span className="text-[10px] px-1.5 py-0.5 bg-zinc-700/50 text-zinc-400 rounded uppercase font-semibold" data-testid={`org-deleted-${org.id}`}>Deleted</span>}
                        {org.is_demo && <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded uppercase font-semibold" data-testid={`org-demo-${org.id}`}>Demo</span>}
                        {!org.is_deleted && org.is_active === false && <span className="text-[10px] px-1.5 py-0.5 bg-rose-500/20 text-rose-400 rounded uppercase font-semibold" data-testid={`org-inactive-${org.id}`}>Inactive</span>}
                        <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded uppercase font-semibold" data-testid={`org-sport-${org.id}`}>{org.sport || 'football'}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-zinc-500 flex-wrap">
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {org.user_count} users</span>
                        {org.admin ? (
                          <span className="flex items-center gap-1 text-emerald-400"><CheckCircle className="w-3 h-3" /> Admin: {org.admin.email}</span>
                        ) : org.pending_admin_invite ? (
                          <span className="flex items-center gap-1 text-amber-400"><Mail className="w-3 h-3" /> Invite pending: {org.pending_admin_invite.email}</span>
                        ) : (
                          <span className="text-rose-400">No admin assigned</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {org.is_deleted ? (
                        <Button variant="ghost" size="sm" onClick={() => handleRestoreOrg(org)} className="text-emerald-500 hover:text-emerald-300 h-8 px-2 text-xs" data-testid={`restore-org-btn-${org.id}`} title="Restore"><Undo2 className="w-3.5 h-3.5 mr-1" />Restore</Button>
                      ) : (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => { setEditOrg(org); setEditName(org.name); setEditSport(org.sport || 'football'); }} className="text-zinc-500 hover:text-zinc-200 h-8 w-8 p-0" data-testid={`edit-org-btn-${org.id}`} title="Edit name & sport"><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleToggleActive(org)} className={`h-8 w-8 p-0 ${org.is_active !== false ? 'text-amber-500 hover:text-amber-300' : 'text-emerald-500 hover:text-emerald-300'}`} data-testid={`toggle-org-btn-${org.id}`} title={org.is_active !== false ? 'Deactivate' : 'Reactivate'}><Power className="w-3.5 h-3.5" /></Button>
                          {!org.admin && !org.pending_admin_invite && <Button variant="ghost" size="sm" onClick={() => setShowInvite(org.id)} className="text-zinc-500 hover:text-zinc-200 h-8 w-8 p-0" data-testid={`invite-admin-btn-${org.id}`} title="Invite Admin"><Mail className="w-3.5 h-3.5" /></Button>}
                          {!org.admin && org.pending_admin_invite && <Button variant="ghost" size="sm" onClick={() => handleResendInvite(org.id)} className="text-amber-500 hover:text-amber-300 h-8 w-8 p-0" data-testid={`resend-invite-btn-${org.id}`} title="Resend Invite"><RotateCw className="w-3.5 h-3.5" /></Button>}
                          {org.admin && <Button variant="ghost" size="sm" onClick={() => setShowInvite(org.id)} className="text-zinc-500 hover:text-zinc-200 h-8 w-8 p-0" data-testid={`change-admin-btn-${org.id}`} title="Change Admin"><UserCog className="w-3.5 h-3.5" /></Button>}
                          <Button variant="ghost" size="sm" onClick={() => handleViewDetail(org.id)} className="text-zinc-500 hover:text-zinc-200 h-8 w-8 p-0" data-testid={`view-org-btn-${org.id}`} title="View details"><ChevronRight className="w-3.5 h-3.5" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(org)} className="text-zinc-600 hover:text-rose-400 h-8 w-8 p-0" data-testid={`delete-org-btn-${org.id}`} title="Delete"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Organization Dialog */}
      <Dialog open={showCreate} onOpenChange={resetCreate}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="create-org-dialog">
          <DialogHeader><DialogTitle>Create Organization</DialogTitle><DialogDescription>Create a new organization and invite its admin.</DialogDescription></DialogHeader>
          {createResult ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 rounded bg-emerald-500/10 border border-emerald-500/20"><CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" /><div><div className="text-sm font-medium text-emerald-300">Organization created</div><div className="text-xs text-zinc-400">{createResult.organization?.name}</div></div></div>
              {createResult.invitation?.email_sent ? (
                <div className="flex items-center gap-2 p-3 rounded bg-emerald-500/10 border border-emerald-500/20" data-testid="create-email-sent"><Mail className="w-4 h-4 text-emerald-400" /><span className="text-sm text-emerald-300">Invitation email sent to {createResult.invitation.email}</span></div>
              ) : (
                <div className="flex items-center gap-2 p-3 rounded bg-amber-500/10 border border-amber-500/20" data-testid="create-email-failed"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-sm text-amber-300">Email failed - share link manually</span></div>
              )}
              <div><div className="text-xs text-zinc-500 mb-1">Invitation link (backup)</div><div className="flex items-center gap-2"><Input value={createResult.invitation?.invite_link || ''} readOnly className="bg-zinc-950 border-zinc-800 text-xs" data-testid="create-invite-link" /><Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(createResult.invitation?.invite_link || ''); toast.success('Copied!'); }}><Copy className="w-4 h-4" /></Button></div></div>
              <Button onClick={resetCreate} className="w-full">Done</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div><label className="text-xs text-zinc-500 mb-1 block">Organization Name *</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. FC Barcelona Academy" className="bg-zinc-950 border-zinc-800" data-testid="create-org-name" /></div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Sport *</label>
                <select
                  value={form.sport}
                  onChange={e => setForm({ ...form, sport: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  data-testid="create-org-sport"
                >
                  {(sports.length > 0 ? sports : [{key:'football',label:'Football'}]).map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
                <p className="text-[10px] text-zinc-600 mt-1">Determines athlete positions, planner session types and icons.</p>
              </div>
              <div className="border-t border-zinc-800 pt-3"><div className="text-xs text-zinc-500 mb-2">Admin Invitation</div><div className="space-y-2"><Input value={form.admin_email} onChange={e => setForm({...form, admin_email: e.target.value})} placeholder="admin@organization.com" type="email" className="bg-zinc-950 border-zinc-800" data-testid="create-admin-email" /><div className="grid grid-cols-2 gap-2"><Input value={form.admin_first_name} onChange={e => setForm({...form, admin_first_name: e.target.value})} placeholder="First name" className="bg-zinc-950 border-zinc-800" data-testid="create-admin-first" /><Input value={form.admin_last_name} onChange={e => setForm({...form, admin_last_name: e.target.value})} placeholder="Last name" className="bg-zinc-950 border-zinc-800" data-testid="create-admin-last" /></div></div></div>
              <Button onClick={handleCreate} disabled={creating} className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="create-org-submit">{creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : 'Create Organization & Send Invite'}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invite Admin Dialog */}
      <Dialog open={!!showInvite} onOpenChange={resetInvite}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="invite-admin-dialog">
          <DialogHeader><DialogTitle>Invite Organization Admin</DialogTitle><DialogDescription>Send an invitation to the admin of this organization.</DialogDescription></DialogHeader>
          {inviteResult ? (
            <div className="space-y-4">
              {inviteResult.email_sent ? <div className="flex items-center gap-2 p-3 rounded bg-emerald-500/10 border border-emerald-500/20" data-testid="invite-email-sent"><Mail className="w-4 h-4 text-emerald-400" /><span className="text-sm text-emerald-300">Invitation sent to {inviteResult.email}</span></div>
              : <div className="flex items-center gap-2 p-3 rounded bg-amber-500/10 border border-amber-500/20"><AlertTriangle className="w-4 h-4 text-amber-400" /><span className="text-sm text-amber-300">Email failed - share link manually</span></div>}
              <div className="flex items-center gap-2"><Input value={inviteResult.invite_link || ''} readOnly className="bg-zinc-950 border-zinc-800 text-xs" /><Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(inviteResult.invite_link || ''); toast.success('Copied!'); }}><Copy className="w-4 h-4" /></Button></div>
              <Button onClick={resetInvite} className="w-full">Done</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Input value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} placeholder="admin@organization.com" type="email" className="bg-zinc-950 border-zinc-800" data-testid="invite-admin-email" />
              <div className="grid grid-cols-2 gap-2"><Input value={inviteForm.first_name} onChange={e => setInviteForm({...inviteForm, first_name: e.target.value})} placeholder="First name" className="bg-zinc-950 border-zinc-800" /><Input value={inviteForm.last_name} onChange={e => setInviteForm({...inviteForm, last_name: e.target.value})} placeholder="Last name" className="bg-zinc-950 border-zinc-800" /></div>
              <Button onClick={handleInviteAdmin} disabled={inviting} className="w-full bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="invite-admin-submit">{inviting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : 'Send Invitation'}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Org Dialog */}
      <Dialog open={!!editOrg} onOpenChange={() => setEditOrg(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm" data-testid="edit-org-dialog">
          <DialogHeader><DialogTitle>Edit Organization</DialogTitle><DialogDescription>Update the organization name and sport.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Name</label>
              <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Organization name" className="bg-zinc-950 border-zinc-800" data-testid="edit-org-name" />
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Sport</label>
              <select
                value={editSport}
                onChange={e => setEditSport(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded-md text-sm text-zinc-100 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="edit-org-sport"
              >
                {(sports.length > 0 ? sports : [{key:'football',label:'Football'}]).map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
              <p className="text-[10px] text-zinc-600 mt-1">Changing sport switches the available positions and planner session types for this org.</p>
            </div>
            <Button onClick={handleEditOrg} disabled={editing} className="w-full" data-testid="edit-org-submit">{editing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Org Detail Dialog */}
      <Dialog open={!!detailOrg} onOpenChange={() => { setDetailOrg(null); setDetailData(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg" data-testid="org-detail-dialog">
          <DialogHeader><DialogTitle>{detailData?.name || 'Organization Details'}</DialogTitle><DialogDescription>High-level organization overview.</DialogDescription></DialogHeader>
          {loadingDetail ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-500" /></div> : detailData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-zinc-500 text-xs">Status</span><div className={`font-medium ${detailData.is_active !== false ? 'text-emerald-400' : 'text-rose-400'}`}>{detailData.is_active !== false ? 'Active' : 'Inactive'}</div></div>
                <div><span className="text-zinc-500 text-xs">Created</span><div className="text-zinc-200">{detailData.created_at ? new Date(detailData.created_at).toLocaleDateString() : '-'}</div></div>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Users ({detailData.users?.length || 0})</h3>
                {detailData.users?.length > 0 ? (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {detailData.users.map(u => (
                      <div key={u.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-zinc-800/30 text-sm" data-testid={`detail-user-${u.id}`}>
                        <div><span className="text-zinc-200">{u.first_name} {u.last_name}</span><span className="text-zinc-500 ml-2 text-xs">{u.email}</span></div>
                        <div className="flex items-center gap-2"><span className="text-[10px] px-1.5 py-0.5 bg-zinc-700/50 rounded text-zinc-300">{u.role}</span>{u.is_active === false && <span className="text-[10px] px-1.5 py-0.5 bg-rose-500/20 rounded text-rose-400">Disabled</span>}</div>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-xs text-zinc-600">No users</p>}
              </div>
              {detailData.pending_invitations?.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Pending Invitations</h3>
                  <div className="space-y-1">{detailData.pending_invitations.map(inv => (
                    <div key={inv.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-amber-500/5 border border-amber-500/10 text-sm"><span className="text-amber-300">{inv.email}</span><span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 rounded text-amber-400">{inv.role}</span></div>
                  ))}</div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-sm" data-testid="delete-org-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-400">Delete Organization</DialogTitle>
            <DialogDescription>This action will remove the organization from the dashboard.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 rounded bg-amber-500/10 border border-amber-500/20 text-sm text-amber-300">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              This will remove <strong>{deleteTarget?.name}</strong> from the dashboard. All users will be disabled. Data will be preserved and can be restored later.
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDeleteTarget(null)} className="flex-1 border-zinc-700">Cancel</Button>
              <Button onClick={() => { handleDeleteOrg(deleteTarget); setDeleteTarget(null); }} className="flex-1 bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-org">Delete Organization</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
